/**
 * Live Production-panel metrics after `SetModuleGraph`, and helpers to capture them
 * onto AI layout cards. Lives under `helpers/` so Three-JS viewer code can import it
 * without pulling in the dashboard module graph.
 *
 * oneDRAW multiplier is always the live SetModuleGraph result for the current layout
 * (including reopened saved designs). Saved card / RetrieveSystemDesign multiplier
 * must not replace that value, and opening a card must not stamp card numbers onto
 * the viewer.
 */

import { useViewerStore } from '../store/store';

const CATEGORY_MAIN_KEY_PREFIX = '__category_main__:';

function getCategoryMainLayoutKey(category: string): string {
    return `${CATEGORY_MAIN_KEY_PREFIX}${category}`;
}

/**
 * Production panel Multiplier from `SetModuleGraph` (`state.multiplier` =
 * module-weighted roof multipliers, 3 decimals). Do not use
 * `moduleMultiplierAverage` — that is the per-module access-band average and
 * does not match Total System Efficiency.
 */
export function readLiveOneDrawSystemMultiplier(state?: {
    multiplier?: unknown;
}): number | null {
    const raw = state?.multiplier ?? useViewerStore.getState().multiplier;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Number(n.toFixed(3));
}

export type LiveLayoutCardMetrics = {
    production: number;
    modules: number;
    efficiency: number | null;
    solarAccess: number | null;
    multiplier: number | null;
};

/**
 * Live Production-panel values after `SetModuleGraph`. Capture these onto AI cards;
 * never write card values back onto the viewer.
 */
export function captureLiveLayoutCardMetrics(state?: {
    actualWattage?: unknown;
    modeCount?: unknown;
    efficiency?: unknown;
    weightedSolarAverage?: unknown;
    multiplier?: unknown;
}): LiveLayoutCardMetrics {
    const s = state ?? (useViewerStore.getState() as any);
    const numOrNull = (v: unknown): number | null =>
        typeof v === 'number' && Number.isFinite(v) ? v : null;
    return {
        production: numOrNull(s.actualWattage) ?? 0,
        modules: numOrNull(s.modeCount) ?? 0,
        efficiency: numOrNull(s.efficiency),
        solarAccess: numOrNull(s.weightedSolarAverage),
        multiplier: readLiveOneDrawSystemMultiplier(s),
    };
}

/** Resolve layout / category keys for the AI design currently applied in the viewer. */
export function resolveActiveAiDesignApplyKeys(): {
    layoutKey: string;
    categoryLabel?: string;
} | null {
    const state = useViewerStore.getState() as any;
    if (!state.isAiGeneratedDesign) return null;

    const selectionKey = String(state.selectedRoofCombinationKey ?? 'default').trim() || 'default';
    if (selectionKey === 'default') {
        return { layoutKey: 'default' };
    }

    const categoryMain = (state.categoryMainLayoutByCategory ?? {}) as Record<string, { key?: string }>;
    const candidatesByCategory = (state.layoutCandidatesByCategory ?? {}) as Record<
        string,
        Array<{ key?: string }>
    >;

    let categoryLabel: string | undefined;
    if (categoryMain[selectionKey]) {
        categoryLabel = selectionKey;
    } else {
        const match = Object.keys(categoryMain).find(
            (cat) => getCategoryMainLayoutKey(cat) === selectionKey,
        );
        categoryLabel = match ?? selectionKey;
    }

    const main = categoryLabel ? categoryMain[categoryLabel] : undefined;
    const candidates = categoryLabel ? candidatesByCategory[categoryLabel] : undefined;
    const layoutKey = main?.key ?? candidates?.[0]?.key ?? selectionKey;

    return { layoutKey, categoryLabel };
}

/**
 * Intentionally a no-op. Opening an AI card must keep the live `SetModuleGraph`
 * Production-panel values. Card metrics are captured at Run AI time, not stamped
 * onto the viewer afterwards.
 */
export function syncLiveViewerMetricsFromCard(_layoutKey: string, _categoryLabel?: string): boolean {
    return false;
    // AI cards store moduleMultiplierAverage. The Production panel, finalize overlay,
    // and oneDRAW save use the roof-weighted `multiplier` from SetModuleGraph. Copying
    // the card value here showed the wrong multiplier until a hard refresh reloaded
    // the backend figure.
}

/**
 * Intentionally a no-op. `SetModuleGraph` is the source of truth for live production,
 * efficiency, solar access, and multiplier. Do not overwrite those from a card.
 */
export function syncActiveAiDesignProductionFromCard(): boolean {
    return false;
}
